import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { t } from '../i18n';

/**
 * Right-side slide-in panel used by the list pages for their "add" forms,
 * replacing the old `window.open('/x/add', '_blank')` new-tab flow.
 *
 * The form inside stays a plain component — it receives onSuccess and knows
 * nothing about the drawer, so the same component still renders on its own
 * /x/add route. Dismissing is the drawer's job: the X, the backdrop, or Esc.
 */
export default function Drawer({ open, onClose, title, width = 'max-w-xl', children }) {
  const panelRef = useRef(null);

  // Esc closes. Bound on document so it works wherever focus currently is.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Stop the list behind the drawer from scrolling while it is open.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Focus the first field so the drawer is usable without reaching for the mouse.
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const firstField = panelRef.current.querySelector(
      'input:not([type="hidden"]), select, textarea'
    );
    if (firstField) firstField.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop — click anywhere outside the panel to dismiss. */}
      <div
        className="absolute inset-0 bg-black/40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative h-full w-full ${width} bg-white shadow-2xl flex flex-col animate-[slideIn_0.2s_ease-out]`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            title={t('Затвори')}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
